import { FinancialStatementSchema, Statement } from '@api';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { WithNAFallback } from '@design/components/table';
import { ColumnTable } from '@terminal/components/column-table';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { useApp } from '@terminal/stores/use-app/useApp';
import clsx from 'clsx';
import { Property } from 'csstype';
import { ReactNode, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MiniBarChart, MiniChartData } from '../mini-bar-chart/MiniBarChart';
import styles from './FinancialTable.module.scss';

export type FinancialTableProps = {
  data: FinancialStatementSchema;
  activeStatementButtonGroupIndex: number;
  handleStatementButtonGroupSelection: (i: number) => void;
  activeTimeframeButtonGroupIndex: number;
  handleTimeframeButtonGroupSelection: (i: number) => void;
  analystEpsEstimatesLabel?: string;
};

interface FinancialTableRow {
  key: string;
  metric: string;
  [timePeriod: `period-${number}`]: string;
  values: MiniChartData[];
  indentation?: number;
  yAxisLabel?: string;
}

type FinancialTableColumnKey = 'metric' | `period-${number}`;

type FinancialTableColumn = {
  key: FinancialTableColumnKey;
  label: string;
  alignment?: Property.TextAlign;
  sticky?: boolean;
  render?: (value: string, item: FinancialTableRow) => ReactNode;
  renderHeader?: () => ReactNode;
};

const PERIOD_EXCLUSIONS = ['LTM', 'NTM'];

export const FinancialTable = ({
  data,
  activeStatementButtonGroupIndex,
  handleStatementButtonGroupSelection,
  activeTimeframeButtonGroupIndex,
  handleTimeframeButtonGroupSelection,
  analystEpsEstimatesLabel,
}: FinancialTableProps) => {
  const { t } = useTranslation('widget');
  const isSidebarExpanded = useApp(state => state.isSidebarExpanded);

  const lastTwoPeriodIndices = new Set(
    data.columns
      .flatMap((c, i) => (PERIOD_EXCLUSIONS.includes(c.period) ? [] : [i]))
      .slice(-2)
  );

  const columns: FinancialTableColumn[] = [
    {
      label: t('widget:financials.dataProviderDetails'),
      key: 'metric',
      sticky: true,
      renderHeader: () => (
        <div className={styles.metricColumnTitleLabelWrapper}>
          <div className={styles.columnTitleLabel}>
            {t('widget:financials.dataProviderDetails', {
              currency: data.metadata.currency,
            })}
          </div>
        </div>
      ),
      render(value, i) {
        return (
          <div className={styles.rowLabelWrapper}>
            <p
              className={styles.rowLabel}
              style={
                i.indentation
                  ? { paddingLeft: `${i.indentation * 20}px` }
                  : undefined
              }
            >
              {value}
            </p>
            <div className={styles.barChartWrapper}>
              <MiniBarChart data={i.values} yAxisLabel={i.yAxisLabel} />
            </div>
          </div>
        );
      },
    },
    ...data.columns.map((col, index) => {
      const key = `period-${index}` as const;

      return {
        label: `${col.year} ${col.period}`,
        key,
        renderHeader: () =>
          !PERIOD_EXCLUSIONS.includes(col.period) ? (
            <TooltipContainer
              label={analystEpsEstimatesLabel}
              disabled={!analystEpsEstimatesLabel}
              offsetY={15}
              inPortal
              withSafePolygon
            >
              <div className={styles.multilineColumnTitleWrapper}>
                <div className={styles.columnTitleLabel}>{col.year}</div>
                <div
                  className={clsx(styles.columnSubtitleLabel, {
                    [styles.dottedUnderline]: lastTwoPeriodIndices.has(index),
                  })}
                >
                  {col.period}
                </div>
              </div>
            </TooltipContainer>
          ) : (
            <div className={styles.columnTitleLabel}>{col.period}</div>
          ),
        render: (value: string) => {
          return (
            <WithNAFallback
              check={!!value}
              value={<span className={styles.numericalValue}>{value}</span>}
            />
          );
        },
      };
    }),
  ];

  const handleRowData = (
    row: Statement,
    indentation?: number
  ): FinancialTableRow => {
    return {
      key: row.display,
      metric: row.display,
      ...row.values_fmt.reduce((a, c, i) => ({ ...a, [`period-${i}`]: c }), {}),
      indentation,
      yAxisLabel: row.value_type === 'percent' ? '%' : undefined,
      values: row.values.reduce((a, value, index) => {
        const current = data.columns[index];
        a.push({
          label: current.period,
          value: value || 0,
          year: current.year,
        });

        return a;
      }, [] as MiniChartData[]),
    };
  };

  const mapRows = (rows: Statement[], level = 0): FinancialTableRow[] => {
    const result: FinancialTableRow[] = [];
    rows.forEach(row => {
      result.push(handleRowData(row, level));

      if (row.subsections && row.subsections.length > 0) {
        row.subsections.forEach(subsection => {
          result.push(...mapRows([subsection], level + 1));
        });
      }
    });
    return result;
  };

  const shadedColumnIndices = data.columns
    .map((column, index) => (column.type === 'expected' ? index + 2 : null))
    .filter(index => index !== null);

  const scopeId = useId();
  const estimatedColumnStyles = useMemo(() => {
    if (!shadedColumnIndices.length) {
      return '';
    }

    return shadedColumnIndices
      .map(
        index =>
          `[data-scope="${scopeId}"] [data-revamp-table] th:nth-child(${index}),
           [data-scope="${scopeId}"] [data-revamp-table] td:nth-child(${index}) {
             background-color: var(--background-table-zebra);
           }`
      )
      .join('\n');
  }, [shadedColumnIndices, scopeId]);

  const rowData: FinancialTableRow[] = mapRows(data.rows);

  return (
    <div className={styles.assetFinancialsRoot} data-testid="financial-table">
      {estimatedColumnStyles && <style>{estimatedColumnStyles}</style>}
      <div className={styles.buttonGroupsWrapper}>
        <div>
          <ButtonGroup
            className={styles.financialStatementStyledButtonGroup}
            labels={[
              t('widget:financials.statementTypes.incomeStatement'),
              t('widget:financials.statementTypes.balanceSheet'),
              t('widget:financials.statementTypes.cashFlow'),
            ]}
            onClick={handleStatementButtonGroupSelection}
            activeIndex={activeStatementButtonGroupIndex}
            customActiveBackgroundColor={'var(--surface-hover)'}
          />
        </div>
        <div>
          <ButtonGroup
            labels={[
              t('widget:financials.timeFrames.annual'),
              t('widget:financials.timeFrames.quarterly'),
            ]}
            onClick={handleTimeframeButtonGroupSelection}
            activeIndex={activeTimeframeButtonGroupIndex}
          />
        </div>
      </div>
      <div
        className={clsx(styles.financialsTableWrapper, {
          [styles.financialsTableWrapperSidebarExpanded]: isSidebarExpanded,
        })}
        data-scope={scopeId}
      >
        <ColumnTable
          columns={columns}
          data={rowData}
          isHeaderSticky
          alignment="right"
        />
      </div>
    </div>
  );
};
